/**
 * Ad-Hoc Capital Amendment Dialog (Capital Redemption)
 * Allows requesting ad-hoc capital withdrawals/injections on Active/Approved/Matured contracts.
 * Creates a pending_changes entry for approval workflow.
 * Includes optional penalty and proof document upload.
 */

import React, { useState, useEffect } from 'react';
import {
  TextField,
  Grid,
  Typography,
  Box,
  Paper,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  TrendingDown as WithdrawalIcon,
  TrendingUp as InjectionIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiPost, apiGet } from '../../utils/apiClient';
import ContractActionDialogBase from './ContractActionDialogBase';
import ScheduleImpactPreviewModal from './ScheduleImpactPreviewModal';
import { useTenant } from '../../contexts/TenantContext';

const AdhocCapitalAmendmentDialog = ({ open, onClose, contract, onSubmitSuccess }) => {
  const { tenant } = useTenant();
  const dp = tenant?.roundingPrecision ?? 3;
  const [formData, setFormData] = useState({
    event_type: 'withdrawal',
    event_date: format(new Date(), 'yyyy-MM-dd'),
    amount: '',
    description: '',
    apply_penalty: false,
    penalty_amount: '',
  });
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        event_type: 'withdrawal',
        event_date: format(new Date(), 'yyyy-MM-dd'),
        amount: '',
        description: '',
        apply_penalty: false,
        penalty_amount: '',
      });
      setReason('');
      setError(null);
      setPreviewData(null);
      setPreviewModalOpen(false);
    }
  }, [open]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (['event_type', 'event_date', 'amount'].includes(field)) {
      setPreviewData(null);
    }
  };

  const handlePreview = async () => {
    if (!formData.amount || !formData.event_date) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const response = await apiGet(
        `/amendments/contract/${contract.id}/preview?event_date=${formData.event_date}&event_type=${formData.event_type}&amount=${formData.amount}`
      );
      if (response?.success) {
        setPreviewData(response.data);
        setPreviewModalOpen(true);
      }
    } catch (err) {
      setError('Failed to generate preview: ' + (err.response?.data?.message || err.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async ({ proof_document_ids }) => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Amount must be greater than zero');
      return;
    }
    // Validate event date is within contract tenor
    if (contract?.maturity_date && new Date(formData.event_date) >= new Date(contract.maturity_date)) {
      setError('Event date must be before the contract maturity date');
      return;
    }
    if (contract?.deposit_date && new Date(formData.event_date) <= new Date(contract.deposit_date)) {
      setError('Event date must be after the contract deposit date');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiPost(`/contracts/${contract.id}/adhoc-capital`, {
        event_date: formData.event_date,
        event_type: formData.event_type,
        amount: parseFloat(formData.amount),
        description: formData.description,
        motivation: reason,
        proof_document_ids,
        penalty_amount: formData.apply_penalty && formData.penalty_amount ? parseFloat(formData.penalty_amount) : null,
      });
      if (response?.success) {
        onClose();
        if (onSubmitSuccess) onSubmitSuccess(response.data);
      } else {
        setError(response?.message || 'Failed to submit amendment request');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to submit amendment request');
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = formData.amount && parseFloat(formData.amount) > 0 && formData.event_date;

  return (
    <>
      <ContractActionDialogBase
        open={open}
        onClose={onClose}
        contract={contract}
        title="Capital Amendment"
        submitLabel="Submit for Approval"
        submitColor={formData.event_type === 'withdrawal' ? 'error' : 'success'}
        isValid={isValid}
        submitting={submitting}
        error={error}
        onClearError={() => setError(null)}
        onSubmit={handleSubmit}
        onPreview={handlePreview}
        showPreview={true}
        previewLoading={previewLoading}
        reason={reason}
        onReasonChange={setReason}
        reasonLabel="Motivation (Required)"
        reasonPlaceholder="Provide a detailed motivation for this amendment. This will be reviewed by an approver."
        reasonHelperText="A motivation is mandatory for capital amendments on active contracts."
      >
        {/* Event Type */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Event Type</Typography>
          <ToggleButtonGroup
            value={formData.event_type}
            exclusive
            onChange={(e, val) => val && handleFieldChange('event_type', val)}
            fullWidth
          >
            <ToggleButton value="withdrawal" color="error">
              <WithdrawalIcon sx={{ mr: 1 }} />
              Capital Withdrawal
            </ToggleButton>
            <ToggleButton value="injection" color="success">
              <InjectionIcon sx={{ mr: 1 }} />
              Capital Injection
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid container spacing={2}>
          <Grid size={6}>
            <TextField
              label="Event Date"
              type="date"
              value={formData.event_date}
              onChange={(e) => handleFieldChange('event_date', e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: contract?.deposit_date?.substring(0, 10),
                max: contract?.maturity_date?.substring(0, 10),
              }}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => handleFieldChange('amount', e.target.value)}
              fullWidth
              required
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Brief description of the capital event"
            />
          </Grid>

          {/* Penalty (withdrawal only) */}
          {formData.event_type === 'withdrawal' && (
            <>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.apply_penalty}
                      onChange={(e) => handleFieldChange('apply_penalty', e.target.checked)}
                    />
                  }
                  label="Apply early redemption penalty"
                />
              </Grid>
              {formData.apply_penalty && (
                <Grid size={6}>
                  <TextField
                    label="Penalty Amount"
                    type="number"
                    value={formData.penalty_amount}
                    onChange={(e) => handleFieldChange('penalty_amount', e.target.value)}
                    fullWidth
                    inputProps={{ min: 0, step: '0.01' }}
                    helperText="Penalty deducted from the withdrawal (recorded for audit)"
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </ContractActionDialogBase>
      <ScheduleImpactPreviewModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        title="Capital Amendment Impact"
        subtitle={`${formData.event_type === 'withdrawal' ? 'Withdrawal' : 'Injection'} of ${formData.amount || 0} on ${formData.event_date}`}
        changeTypeLabel={formData.event_type === 'withdrawal' ? 'Capital Withdrawal' : 'Capital Injection'}
        previewData={previewData}
        contract={contract}
      />
    </>
  );
};

export default AdhocCapitalAmendmentDialog;
